import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SkeletonCard, SkeletonLine } from '@origo/origo-ui';
import PlanDetailHeader from './PlanDetailHeader';
import styles from './PlanDetailSkeleton.module.css';

/** Skjelettet vises først etter denne forsinkelsen, så raske cache-treff ikke blinker. */
const SHOW_DELAY_MS = 300;

interface PlanDetailSkeletonProps {
    title: string;
    subtitle?: string;
    /** `form` for skjemasider (plan-info), `list` for kortlister (behandlinger, dokumenter o.l.). */
    variant: 'form' | 'list';
}

const Field: React.FC<{ inputHeight?: string }> = ({ inputHeight = 'var(--control-height-md)' }) => (
    <div className={styles.field}>
        <SkeletonLine width={120} height={12} />
        <SkeletonLine width="100%" height={inputHeight} radius="md" />
    </div>
);

const FormSkeleton: React.FC = () => (
    <>
        <div className={styles.section}>
            <SkeletonLine width={160} height={20} />
            <Field />
            <div className={styles.twoColumn}>
                <Field />
                <Field />
            </div>
        </div>
        <div className={styles.section}>
            <SkeletonLine width={160} height={20} />
            <Field />
            <Field inputHeight="calc(var(--control-height-md) * 3)" />
        </div>
    </>
);

const ListSkeleton: React.FC = () => (
    <>
        <SkeletonLine width={120} height={20} />
        {Array.from({ length: 4 }, (_, i) => (
            <SkeletonCard key={i}>
                <SkeletonLine width="min(50%, 320px)" />
                <div className={styles.metaRow}>
                    <SkeletonLine width="min(30%, 160px)" height={12} />
                    <SkeletonLine width={64} height={20} pill />
                </div>
            </SkeletonCard>
        ))}
    </>
);

/**
 * Lastetilstand for plan-detaljfanene: ekte sideheader + skjelett formet som
 * innholdet, i stedet for en ensom spinner (se Skeleton.docs.md).
 */
const PlanDetailSkeleton: React.FC<PlanDetailSkeletonProps> = ({ title, subtitle, variant }) => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const id = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
        return () => window.clearTimeout(id);
    }, []);

    return (
        <article className={styles.page}>
            <PlanDetailHeader title={title} subtitle={subtitle} />
            <div
                className={variant === 'form' ? styles.formBody : styles.body}
                role="status"
                aria-label={t('common.loading')}
            >
                {visible && (variant === 'form' ? <FormSkeleton /> : <ListSkeleton />)}
            </div>
        </article>
    );
};

export default PlanDetailSkeleton;
